import { ChangeEvent, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Close, EmailOutlined } from '@mui/icons-material';
import { QTextField } from '@kw/quokka-ui';
import { useSnackbar } from 'notistack';
import { MutatorOptions } from 'swr';
import { validateEmail } from '../../../utils/emailValidation';
import { sendHttpRequest } from '../../../utils/network.service';
import { OrganizationSettingCard } from './OrganizationSettingCard';
import {
  OrganizationSettingsDialog,
  OrganizationSettingsFieldLabel,
  OrganizationSettingsSnackbarDismissButton,
  OrganizationSettingsValidationMessage,
} from './OrganizationSettingsDialog';

export const DEFAULT_SUPPORT_EMAIL = 'support@quokka.io';

interface CustomSupportEmailCardProps {
  isAdmin: boolean;
  currentSupportEmail?: string;
  lastUpdated?: string;
  mutate: (data?: unknown, opts?: boolean | MutatorOptions<unknown>) => Promise<unknown>;
}

export const CustomSupportEmailCard = ({ isAdmin, currentSupportEmail, lastUpdated, mutate }: CustomSupportEmailCardProps) => {
  const organizationSupportEmail = currentSupportEmail?.trim() || DEFAULT_SUPPORT_EMAIL;
  const [savedSupportEmail, setSavedSupportEmail] = useState(organizationSupportEmail);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [pendingSupportEmail, setPendingSupportEmail] = useState(organizationSupportEmail);
  const [emailTouched, setEmailTouched] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [localLastUpdated, setLocalLastUpdated] = useState<string | Date | undefined>(lastUpdated);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  useEffect(() => {
    setSavedSupportEmail(organizationSupportEmail);
  }, [organizationSupportEmail]);

  useEffect(() => {
    if (!isEditorOpen) setPendingSupportEmail(savedSupportEmail);
  }, [isEditorOpen, savedSupportEmail]);

  useEffect(() => {
    if (lastUpdated) setLocalLastUpdated(lastUpdated);
  }, [lastUpdated]);

  const trimmedSupportEmail = pendingSupportEmail.trim();
  const isEmailValid = validateEmail(trimmedSupportEmail);
  const isSaveDisabled = isSaving || !isEmailValid || trimmedSupportEmail === savedSupportEmail;

  const closeEditor = () => {
    if (isSaving) return;

    setIsEditorOpen(false);
    setPendingSupportEmail(savedSupportEmail);
    setEmailTouched(false);
  };

  const openEditor = () => {
    setPendingSupportEmail(savedSupportEmail);
    setEmailTouched(false);
    setIsEditorOpen(true);
  };

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPendingSupportEmail(event.target.value);
    setEmailTouched(true);
  };

  const updateSupportEmail = async () => {
    setEmailTouched(true);
    if (isSaveDisabled) return;

    setIsSaving(true);
    const result = await sendHttpRequest({
      path: `${process.env.ORGANIZATION_SERVICE_URL}/organizations/`,
      method: 'PUT',
      body: { supportEmail: trimmedSupportEmail },
      successMessage: `Support email set to ${trimmedSupportEmail}.`,
      errorMessage: 'Error updating support email.',
      enqueueSnackbar,
      successOptions: {
        action: snackbarId => (
          <OrganizationSettingsSnackbarDismissButton
            aria-label="Dismiss notification"
            size="small"
            onClick={() => closeSnackbar(snackbarId)}
          >
            <Close fontSize="small" />
          </OrganizationSettingsSnackbarDismissButton>
        ),
      },
    });
    setIsSaving(false);

    if (result.error) return;

    setSavedSupportEmail(trimmedSupportEmail);
    setPendingSupportEmail(trimmedSupportEmail);
    setLocalLastUpdated(new Date());
    setIsEditorOpen(false);
    setEmailTouched(false);
    await mutate();
  };

  return (
    <>
      <OrganizationSettingCard
        title="Support Email"
        description="Enter the email address where Q-scout mobile app user support requests should be sent."
        icon={<EmailOutlined />}
        valueLabel="Email"
        value={savedSupportEmail}
        lastUpdated={localLastUpdated}
        onEdit={isAdmin ? openEditor : undefined}
      />

      <OrganizationSettingsDialog
        id="edit-support-email"
        open={isEditorOpen}
        title="Edit support email"
        body="In-app support requests from Q-scout mobile app users enrolled in your organization are sent to this address."
        primaryLabel="Save"
        onPrimary={updateSupportEmail}
        onClose={closeEditor}
        showCancel
        isSaving={isSaving}
        primaryDisabled={isSaveDisabled}
      >
        <OrganizationSettingsFieldLabel as="label" htmlFor="support-email-input">
          Email
        </OrganizationSettingsFieldLabel>
        <EmailField
          id="support-email-input"
          inputProps={{ 'aria-label': 'Email' }}
          value={pendingSupportEmail}
          onChange={handleEmailChange}
          placeholder="support@example.com"
          type="email"
          autoFocus
          error={emailTouched && !isEmailValid}
          fullWidth
          disabled={isSaving}
        />
        {emailTouched && !isEmailValid && (
          <OrganizationSettingsValidationMessage role="alert">Enter a valid email address.</OrganizationSettingsValidationMessage>
        )}
      </OrganizationSettingsDialog>
    </>
  );
};

const EmailField = styled(QTextField)`
  .MuiOutlinedInput-root {
    border-radius: 8px;
  }
`;
